import { PurchasedEnergyType } from '../../generated/graphql'
import { getEmissionCoefficient } from '../../emission-coefficients/emission-coefficient-calculator'
import { reduceToSum } from '../../util/array-utility'
import { EmissionModel } from '../../emission-coefficients/emission-coefficients'
import { calculateTotalCarbonFootprint } from './carbon-footprint-calculator'
import { PurchasedEnergyResultSubCategory } from '../../provider/result-categories'
import { getFilteredResultsFromContainers } from '../calculator-result-utility'
import { CalculatedVersion } from '../use-calculator'
import { M } from '../../util/formatters'

export type EnergyResult = {
  type: PurchasedEnergyType
  total: number
}

export function calculateEnergyCarbonFootprint(
  energies: EnergyResult[],
  year: number,
): number {
  return energies
    .map(({ type, total }) => (total * getEmissionCoefficient(type, year)) / M)
    .reduce(reduceToSum, 0)
}

export function calculateTotalEnergyCarbonFootprint(
  energies: EnergyResult[],
  emissionModel: EmissionModel,
): number {
  const mapYearIndexToValue = (yearIndex: number) =>
    calculateEnergyCarbonFootprint(
      energies,
      emissionModel.buildingYear + yearIndex,
    )

  return calculateTotalCarbonFootprint(mapYearIndexToValue, emissionModel)
}

export function calculateInherentEnergyCarbonFootprint(
  energies: EnergyResult[],
  emissionModel: EmissionModel,
  area: number,
): number {
  const totalFootprint = calculateTotalEnergyCarbonFootprint(
    energies,
    emissionModel,
  )

  return totalFootprint / (area * emissionModel.inspectionLengthInYears)
}

export function calculateInherentFirstYearEnergyCarbonFootprint(
  energies: EnergyResult[],
  emissionModel: EmissionModel,
  area: number,
): number {
  const firstYearFootprint = calculateEnergyCarbonFootprint(
    energies,
    emissionModel.buildingYear,
  )

  return firstYearFootprint / area
}

function getResult(
  { energyCalculator, totalArea, buildingIdsToAreas }: CalculatedVersion,
  subCategories: PurchasedEnergyResultSubCategory[],
  calculateResult: (
    totalEnergies: EnergyResult[],
    model: EmissionModel,
    area: number,
  ) => number,
  emissionModel: EmissionModel,
  selectedBuildingIds?: string[],
): number | null {
  const containers = selectedBuildingIds
    ? energyCalculator
        .getNonMultipliedBuildings()
        .filter((building) => selectedBuildingIds.includes(building.id))
    : energyCalculator.getAllContainers()

  if (selectedBuildingIds && containers.length === 0) {
    return null
  }

  const energyBus = selectedBuildingIds
    ? null
    : energyCalculator.findEnergyBus()

  const energies = subCategories.map((subCategory) => ({
    type: subCategory.purchasedEnergyType,
    total: getFilteredResultsFromContainers(subCategory, containers, energyBus)
      .total,
  }))

  const area = selectedBuildingIds
    ? selectedBuildingIds
        .map((buildingId) => buildingIdsToAreas[buildingId] ?? 0)
        .reduce(reduceToSum, 0)
    : totalArea

  return calculateResult(energies, emissionModel, area)
}

export function getEnergyResults(
  versions: CalculatedVersion[],
  subCategories: PurchasedEnergyResultSubCategory[],
  calculateResult: (
    totalEnergies: EnergyResult[],
    model: EmissionModel,
    area: number,
  ) => number,
  emissionModel: EmissionModel,
  selectedBuildingIds?: string[],
): (number | null)[] {
  return versions.map((version) =>
    getResult(
      version,
      subCategories,
      calculateResult,
      emissionModel,
      selectedBuildingIds,
    ),
  )
}
